import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  getLocalizedPath,
  extractLocaleFromPath,
  cleanupLocalePath,
  hasLocalePrefix,
} from '@/utils/locale';
import { ROUTES } from '@/constants/route-config';

/**
 * Validates if the URL contains a valid locale
 * If not, redirects to the default locale path
 */
export const LocaleValidator: React.FC = () => {
  const location = useLocation();
  const { i18n } = useTranslation();

  //clean up the path to remove any duplicate locale prefixes
  const cleanPath = cleanupLocalePath(location.pathname);

  if (cleanPath !== location.pathname) {
    return (
      <Navigate
        to={{
          pathname: cleanPath,
          search: location.search,
        }}
        replace
      />
    );
  }

  const { locale } = extractLocaleFromPath(cleanPath);
  if (!locale) {
    return <Outlet />;
  }

  // Get all simplified language codes for comparison
  const simplifiedLanguages = i18n.languages
    .map((lang) => lang.split('-')[0])
    .filter((lang, index, self) => self.indexOf(lang) === index); // Remove duplicates

  const isValidLocale = simplifiedLanguages.includes(locale);

  if (isValidLocale) {
    return <Outlet />;
  }

  const defaultLocale = i18n.language.split('-')[0];
  const pathWithoutLocale = cleanPath.replace(`/${locale}`, '') || '/';
  const newPath = cleanupLocalePath(`/${defaultLocale}${pathWithoutLocale}`);

  return (
    <Navigate
      to={{
        pathname: newPath,
        search: location.search,
      }}
      replace
    />
  );
};

/**
 * Sends a locale-only path to the right landing page for the current session.
 * Signed-in users land on Dashboard; visitors land on Discover.
 */
export const LocaleBaseRedirect: React.FC = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();

  const { locale } = extractLocaleFromPath(location.pathname);
  const simpleLang = locale
    ? locale.split('-')[0]
    : i18n.language.split('-')[0];
  if (isLoading) return null;

  const landingPath = getLocalizedPath(
    isAuthenticated ? ROUTES.DASHBOARD : ROUTES.DISCOVER,
    simpleLang,
  );

  if (location.pathname === landingPath) {
    return <Outlet />;
  }

  return (
    <Navigate
      to={{
        pathname: landingPath,
        search: location.search,
      }}
      replace
    />
  );
};

/**
 * Handles /path -> /locale/path redirects
 * Example: /dashboard -> /en/dashboard
 */
export const BasePathRedirect: React.FC = () => {
  const { i18n } = useTranslation();
  const location = useLocation();

  const simpleLang = i18n.language.split('-')[0];

  if (hasLocalePrefix(location.pathname)) {
    return <Outlet />;
  }

  const newPath = `/${simpleLang}${location.pathname}`;

  return (
    <Navigate
      to={{
        pathname: cleanupLocalePath(newPath), // Clean up any potential duplicate locales
        search: location.search,
      }}
      replace
    />
  );
};

/**
 * Handles / -> the session-appropriate localized landing page.
 * Skips redirect if Auth0 callback parameters are present
 */
export const RootRedirect: React.FC = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();
  const simpleLang = i18n.language.split('-')[0];

  // Don't redirect if Auth0 callback parameters are present
  // Let Auth0 SDK process the callback first
  const hasAuth0Params = location.search.includes('code=') && location.search.includes('state=');
  if (hasAuth0Params) {
    return <Outlet />;
  }

  if (isLoading) return null;

  const landingPath = getLocalizedPath(
    isAuthenticated ? ROUTES.DASHBOARD : ROUTES.DISCOVER,
    simpleLang,
  );

  return <Navigate to={landingPath} replace />;
};
